import { useTheme } from '@mui/material';

import { IDateAndValue } from 'models';
import { selectCurrentUserInfo } from 'store/slices';
import { useAppSelector } from 'store/store.hook';
import { sortStringArray } from 'utils';
import { useFetchScansCountQuery, useFetchScheduleProgressQuery, useFetchSubscribersCountQuery } from './metrics.api';

type UseScansCountProps = {
  buildingId: number;
  start: string;
  end: string;
};

export const useScansCount = ({ buildingId, start, end }: UseScansCountProps) => {
  const theme = useTheme();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const { data: _scans = [], ...rest } = useFetchScansCountQuery({ orgSlug, buildingId, start, end });

  let scansSource = [];

  if (Array.isArray(_scans)) {
    scansSource = _scans;
  } else if (Array.isArray(_scans?.scans)) {
    scansSource = _scans.scans;
  } else if (_scans && typeof _scans === 'object') {
    scansSource = Object.entries(_scans).map(([date, value]) => ({ date, value }));
  }

  const scans = scansSource.map((item: any) => {
    const { date = '', value = 0 } = item;

    return { date, value };
  });

  const labels = scans.map((item: any) => item.date);
  const values = scans.map((item: any) => item.value);

  const data = {
    labels,
    datasets: [{ label: 'Scans #', data: values, backgroundColor: `${theme.palette.primary.main}A0` }],
  };

  return { data, ...rest };
};

type UseSubscribersCountProps = {
  buildingId: number;
};

export const useSubscribersCount = ({ buildingId }: UseSubscribersCountProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const { data: { count } = { count: 0 }, ...rest } = useFetchSubscribersCountQuery({ orgSlug, buildingId });

  const data = {
    labels: ['Subscribers', ''],
    datasets: [
      {
        label: '# of Votes',
        data: [count, 100 - count < 0 ? 0 : 100 - count],
        backgroundColor: ['#FF8900', '#CFD8DC'],
        borderWidth: 1,
      },
    ],
  };

  return { data, ...rest };
};

type UseScheduleProgressProps = {
  buildingId: number;
};

export const useScheduleProgress = ({ buildingId }: UseScheduleProgressProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const { data: _schedules = {}, ...rest } = useFetchScheduleProgressQuery({ orgSlug, buildingId });

  const schedules: Array<IDateAndValue> = Object.keys(_schedules)
    .sort(sortStringArray)
    .reduce((prev: any, curr: any) => {
      const keyNumber = Number(curr) > 12 ? Number(curr) - 12 : Number(curr);
      const key = `${keyNumber}`.length === 1 ? `0${keyNumber}` : `${keyNumber}`;

      return [...(prev || []), { date: `${curr}:00`, value: _schedules[key] }];
    }, []);

  const labels = schedules.map((item: any) => item.date);
  const values = schedules.map((item: any) => item.value);

  const data = {
    labels,
    values,
  };

  return { data, ...rest };
};
